"use client";

import { c as _c } from "react/compiler-runtime";
import type { Genre, Premiere } from "@filmweb/sanity-types";
import { compareAsc, parseISO } from "date-fns";
import { useState, useSyncExternalStore, type CSSProperties } from "react";
import Button from "src/components/button/Button";
import NoShows from "src/components/film/program/NoShows";
import Show from "src/components/film/program/Show";
import NoLocation from "src/components/program/NoLocation";
import { useLocationContext, useSelectedCinemaContext } from "src/lib/contexts/ContextTypes";
import { type Show as ShowType } from "src/lib/movieinfo/movieinfotypes";
import styles from './Shows.module.css';
import classNames from 'classnames';
import Kinoklubb from "src/components/kinoklubb/Kinoklubb";
import { locale } from "@locale";
import getThemeStyles from "src/lib/styling/getThemeStyles";
import { useProgramSearchParams } from "src/lib/program/useProgramSearchParams";

//#region [Props]
type ShowsProps = {
  edi?: string | null;
  movieTitle: string;
  premiere?: Premiere | null;
  genres?: Genre[];
  isKinoklubbMovie?: boolean;
  hasKinoklubbShows?: boolean;
  shows?: ShowType[];
  /** false where the cinema filter isn't offered: the selected cinemas are kept, but ignored here */
  applyCinemaFilter?: boolean;
};
//#endregion

//#region [Component]
export default function Shows(t0) {
  const $ = _c(38);
  const {
    edi,
    movieTitle,
    premiere,
    genres,
    isKinoklubbMovie,
    hasKinoklubbShows,
    shows,
    applyCinemaFilter: t1
  } = t0;
  const applyCinemaFilter = t1 === undefined ? true : t1;
  const [userExpanded, setUserExpanded] = useState(false);
  const locationContext = useLocationContext();
  const cinemaContext = useSelectedCinemaContext();
  const selectedCinemas = cinemaContext?.selectedCinemas;
  const {
    theatre: theatreParam
  } = useProgramSearchParams();
  let t2;
  if ($[0] !== applyCinemaFilter || $[1] !== selectedCinemas || $[2] !== theatreParam) {
    t2 = theatreParam ? [theatreParam] : applyCinemaFilter ? selectedCinemas : null;
    $[0] = applyCinemaFilter;
    $[1] = selectedCinemas;
    $[2] = theatreParam;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const activeCinemas = t2;
  let filteredShows = [];
  let upcomingShowCount = 0;
  if ((shows?.length ?? 0) > 0) {
    filteredShows = shows.filter(_temp) as ShowType[];
    upcomingShowCount = filteredShows.length;
    if ((activeCinemas?.length ?? 0) > 0) {
      let t3;
      if ($[4] !== activeCinemas) {
        t3 = show_0 => activeCinemas.includes(show_0.theaterName);
        $[4] = activeCinemas;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      filteredShows = filteredShows.filter(t3);
    }
  }
  const isFilteredByCinema = !theatreParam && upcomingShowCount > 0 && filteredShows.length === 0;
  const isWide = useSyncExternalStore(_temp2, _temp3, _temp4);
  const showAllButton = !userExpanded && (filteredShows.length > 9 || !isWide && filteredShows.length > 6);
  if (!locationContext?.location) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <NoLocation />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const numRows = !showAllButton ? Math.ceil((filteredShows?.length ?? 0) / 3) : 3;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    const themeStyles = getThemeStyles(styles);
    t3 = classNames(themeStyles);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = showAllButton === false;
  let t5;
  if ($[8] !== t4) {
    t5 = classNames(styles.SShowWrapper, {
      [styles.ShowAllShows]: t4
    });
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== numRows) {
    t6 = {
      "--prog-rows": numRows
    };
    $[10] = numRows;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = t6 as CSSProperties;
  let t8;
  if ($[12] !== edi || $[13] !== movieTitle || $[14] !== premiere || $[15] !== shows?.length) {
    t8 = (shows?.length ?? 0) === 0 && <NoShows edi={edi} movieTitle={movieTitle} premiere={premiere} className={styles.NoShows} />;
    $[12] = edi;
    $[13] = movieTitle;
    $[14] = premiere;
    $[15] = shows?.length;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== edi || $[18] !== isFilteredByCinema || $[19] !== movieTitle || $[20] !== premiere) {
    t9 = isFilteredByCinema && <NoShows edi={edi} movieTitle={movieTitle} premiere={premiere} className={styles.NoShows} isFilteredByCinema={true} />;
    $[17] = edi;
    $[18] = isFilteredByCinema;
    $[19] = movieTitle;
    $[20] = premiere;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = (filteredShows.length ?? 0) > 0 && <>{filteredShows.map((show_1, index) => <Show key={`${show_1.ticketSaleUrl?.trim() ?? index}`} show={show_1} genres={genres} isKinoklubbMovie={isKinoklubbMovie} className={styles.SShow} />)}</>;
  let t11;
  if ($[22] !== t10 || $[23] !== t5 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t11 = <div className={t5} style={t7}>{t8}{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== hasKinoklubbShows || $[29] !== isKinoklubbMovie) {
    t12 = hasKinoklubbShows && isKinoklubbMovie && <Kinoklubb className={styles.Kinoklubb} />;
    $[28] = hasKinoklubbShows;
    $[29] = isKinoklubbMovie;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== showAllButton) {
    t13 = showAllButton && <Button className={styles.SShowAllButton} onClick={() => setUserExpanded(true)} text={locale.program.movie.showAll} fwVariant="outlineGradient1" dkVariant="darkOutline" />;
    $[31] = showAllButton;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t3) {
    t14 = <div className={t3}>{t11}{t12}{t13}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t3;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
//#endregion
function _temp4() {
  return false;
}
function _temp3() {
  return window.matchMedia("(min-width: 550px)").matches;
}
function _temp2(cb) {
  const mq = window.matchMedia("(min-width: 550px)");
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}
function _temp(show) {
  return compareAsc(parseISO(show.showStart), new Date()) > 0;
}